import {
  UserAgent,
  Invitation,
  Registerer,
  TransportState,
  RegistererState,
  type Core,
  type RegistererOptions,
} from "sip.js";
import type { UserAgentOptions } from "sip.js";
import { useSipStore } from "../store/sip";
import type { SipCredentials } from "@/types/sipCredentials";
import type { SipLogConnector } from "@/components/SIPIntegration/composables/useSipLogger";
import { monitorSession } from "@/sip/sip-call-manager";
import { sipTelemetry } from "./sipTelemetry";
import {
  postIncomingCall,
  postIncomingLead,
} from "@/components/SIPIntegration/composables/useSipBroadcast";
import { buildIncomingCallPayload } from "@/sip/sipCommandRouter";
import { shouldRejectInvite } from "@/sip/sipInviteGuard";
import { resolveIncomingLeadUrl } from "@/sip/incomingLeadLookup";

const REGISTRATION_TIMEOUT_MS = 8_000;

const REGISTRATION_TIMEOUT_SENTINEL = Symbol("sip-registration-timeout");
const TRANSPORT_DROPPED_SENTINEL = Symbol("sip-transport-dropped");

const TRANSPORT_FAILURE_MESSAGE =
  "Impossibile connettersi al centralino. Possibile problema di rete o di certificato TLS sul server. " +
  "Contatta l'amministratore se persiste.";
const TIMEOUT_FAILURE_MESSAGE =
  "Il centralino non ha risposto entro il tempo previsto. Riprova o contatta l'amministratore.";
const TRANSPORT_DROP_MESSAGE = "Centralino non raggiungibile. Verifica la connessione.";

let userAgent: UserAgent | null = null;
let sipStore: ReturnType<typeof useSipStore>;
let registerer: Registerer | null = null;
let wasEverConnected = false;
let activeLogConnector: SipLogConnector | undefined = undefined;
// True mentre stiamo deregistrando di proposito (logout, cambio ruolo, yield
// dell'owner): serve a distinguere una deregistrazione voluta da una caduta
// silenziosa della registrazione (refresh throttlato in background), così solo
// quest'ultima innesca il flusso di failure/retry. Vedi GAIA-2587.
let intentionalUnregister = false;

const REGISTRATION_LOST_MESSAGE =
  "Registrazione al centralino scaduta. Riconnessione in corso… Se persiste, ricarica la barra.";

/** Register a log connector so that all SIP.js internal messages are forwarded to it. */
export const setSipLogConnector = (connector: SipLogConnector | undefined): void => {
  activeLogConnector = connector;
};

/**
 * Se il chiamante è un contatto già esistente in Gaia, annuncia la sua scheda.
 *
 * Fire & forget di proposito: la notifica di chiamata in arrivo è GIÀ partita e
 * non deve dipendere da una GET. Se la lookup fallisce o il numero non è in
 * anagrafica, l'operatore vede il banner senza il bottone — mai un banner in meno.
 *
 * Gira solo nella finestra owner (è lei che riceve l'INVITE), quindi una sola
 * lookup per chiamata anche con N tab aperte. Il risultato viene sia messo nello
 * store locale — per il pop-up dentro la barra — sia pubblicato sul canale
 * `gaia-sip`, che è il solo modo per raggiungere la tab in cui l'operatore
 * lavora davvero (GAIA-2719).
 */
const announceCallerLead = (phoneNumber: string): void => {
  if (!phoneNumber) return;

  resolveIncomingLeadUrl(phoneNumber)
    .then((targetUrl) => {
      if (!targetUrl) return;
      sipStore.targetUrl = targetUrl;
      sipStore.persistRecoverySnapshot();
      postIncomingLead(phoneNumber, targetUrl);
    })
    .catch((err) => console.warn("[SIP] lookup scheda del chiamante fallita:", err));
};

export const setupSIP = async (credentials: SipCredentials = {}) => {
  sipStore = useSipStore();
  // Nuova sessione: qualsiasi deregistrazione futura torna a essere "sospetta"
  // finché non la marchiamo esplicitamente come voluta in deregisterSIP.
  intentionalUnregister = false;

  const sipUrl = import.meta.env.VITE_SIP_URL ?? "asterisk.harleydikkinson.com";
  const sipWss = `wss://${sipUrl}/ws`;
  const sipDisplayName = credentials?.displayName ?? "H&D";
  const sipInterno = credentials?.username ?? "220";
  const sipPassword = credentials?.password ?? "ki87t5e3";
  const sipUri = `sip:${sipInterno}@${sipUrl}:5060`;

  const uri = UserAgent.makeURI(sipUri);
  if (!uri) {
    await deregisterSIP();
    throw new Error("URI non valido per il SIP UserAgent");
  }

  const userAgentOptions: UserAgentOptions = {
    uri,
    transportOptions: { server: sipWss },
    logLevel: activeLogConnector ? "debug" : "error",
    logConnector: activeLogConnector,
    authorizationUsername: sipInterno,
    authorizationPassword: sipPassword,
    displayName: sipDisplayName,
    noAnswerTimeout: 30,
  };

  if (!userAgent) {
    userAgent = new UserAgent(userAgentOptions);
    userAgent.transport.stateChange.addListener((state) => {
      if (state === TransportState.Connected) {
        wasEverConnected = true;
      } else if (state === TransportState.Disconnected && wasEverConnected) {
        sipStore.setConnectionFailure({
          type: "transport",
          message: TRANSPORT_DROP_MESSAGE,
        });
        sipTelemetry.connectionFailed({ failureType: "transport", transaction: "transport" });
      }
    });
  }

  const registererOptions: RegistererOptions = { expires: 600 };
  registerer = new Registerer(userAgent, registererOptions);

  registerer.stateChange.addListener((state) => {
    if (state === RegistererState.Registered) {
      sipStore.isRegistered = true;
      sipStore.clearConnectionFailure();
      return;
    }

    // Caduta NON intenzionale della registrazione: il refresh periodico (che
    // SIP.js schedula via setTimeout) è stato throttlato/congelato mentre il
    // popup era in background, oppure il re-REGISTER è stato rifiutato. Su
    // Asterisk il binding resta finché non scade (expires 600s): l'interno
    // continua a ricevere chiamate in coda ma la barra è cieca → chiamate perse
    // "terminate da Sistema" con durata 0:00 (GAIA-2587). Trasformiamo la morte
    // silenziosa in un failure visibile che innesca banner + auto-retry.
    if (
      (state === RegistererState.Unregistered || state === RegistererState.Terminated) &&
      !intentionalUnregister &&
      sipStore.isRegistered
    ) {
      sipTelemetry.registrationLost({
        fromState: state,
        visibility: typeof document !== "undefined" ? document.visibilityState : "visible",
      });
      // setConnectionFailure imposta anche isRegistered=false: la state machine
      // di useSipConnectionRetry (che osserva connectionFailure) programma il
      // riaggancio automatico, dato che eravamo già registrati.
      sipStore.setConnectionFailure({
        type: "transport",
        message: REGISTRATION_LOST_MESSAGE,
      });
    }
  });

  userAgent.delegate = {
    onInvite: (invitation: Invitation) => {
      if (
        shouldRejectInvite({
          isInCall: sipStore.isInCall,
          hasIncomingCall: sipStore.incomingCall !== null,
        })
      ) {
        invitation
          .reject({ statusCode: 486 })
          .catch((err) => console.error("Errore rifiutando INVITE concorrente:", err));
        return;
      }
      sipStore.setIncomingCall(invitation);
      const user = invitation.remoteIdentity?.uri?.user;
      const { phoneNumber } = buildIncomingCallPayload(user);
      // La linea la calcola già setIncomingCall: si rilegge dallo store invece di
      // ricavarla due volte dallo stesso INVITE.
      postIncomingCall(phoneNumber, sipStore.callerLine);
      announceCallerLead(phoneNumber);
      monitorSession(invitation, "incoming");
    },
  };

  // Listener locali per la race: vanno rimossi sempre (success/failure) per evitare
  // accumulo cross-retry sul transport singleton di SIP.js.
  const registeredListeners: Array<() => void> = [];
  const addRegisteredListener = <T>(
    target: {
      addListener: (l: (s: T) => void) => void;
      removeListener: (l: (s: T) => void) => void;
    },
    listener: (s: T) => void
  ): void => {
    target.addListener(listener);
    registeredListeners.push(() => target.removeListener(listener));
  };
  const cleanupRaceListeners = (): void => {
    while (registeredListeners.length > 0) {
      const off = registeredListeners.pop();
      try {
        off?.();
      } catch {
        /* no-op */
      }
    }
  };

  // Promise che si risolve quando il registerer raggiunge davvero lo stato Registered.
  // Serve perché register() può risolvere prima (transazione SIP completata) anche se
  // il server non ha confermato la registrazione: senza questa attesa avremmo un falso positivo.
  const registeredPromise = new Promise<void>((resolve) => {
    addRegisteredListener<RegistererState>(registerer.stateChange, (state) => {
      if (state === RegistererState.Registered) resolve();
    });
  });

  // Se il trasporto cade durante il primo handshake (cert scaduto / TLS rifiutato),
  // ce ne accorgiamo qui senza aspettare il timeout completo.
  const transportDropPromise = new Promise<typeof TRANSPORT_DROPPED_SENTINEL>((resolve) => {
    addRegisteredListener<TransportState>(userAgent.transport.stateChange, (state) => {
      if (state === TransportState.Disconnected && !wasEverConnected) {
        resolve(TRANSPORT_DROPPED_SENTINEL);
      }
    });
  });

  let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
  const timeoutPromise = new Promise<typeof REGISTRATION_TIMEOUT_SENTINEL>((resolve) => {
    timeoutHandle = setTimeout(
      () => resolve(REGISTRATION_TIMEOUT_SENTINEL),
      REGISTRATION_TIMEOUT_MS
    );
  });

  try {
    await userAgent.start();
    await registerer.register({
      requestDelegate: {
        onReject: (response: Core.IncomingResponse) => {
          const code = response.message.statusCode;
          const retryAfterRaw = response.message.getHeader("retry-after");
          const retryAfterParsed = retryAfterRaw ? Number.parseInt(retryAfterRaw, 10) : NaN;
          const retryAfter = Number.isNaN(retryAfterParsed) ? undefined : retryAfterParsed;
          if (code === 401 || code === 403 || code === 407) {
            sipStore.setConnectionFailure({
              type: "auth",
              message: "Credenziali SIP non valide. Controlla il login.",
            });
            sipTelemetry.connectionFailed({
              failureType: "auth",
              sipCode: code,
              transaction: "register",
              retryAfter,
            });
          } else {
            sipStore.setConnectionFailure({
              type: "transport",
              message: `Registrazione rifiutata (codice ${code ?? "sconosciuto"}). Riprova più tardi.`,
              // Rifiuto transitorio (es. 503 Service Unavailable): se il server
              // indica un Retry-After lo passiamo alla state machine di retry,
              // che lo usa come delay dell'auto-retry (GAIA-2589).
              retryAfterMs: retryAfter !== undefined ? retryAfter * 1_000 : undefined,
            });
            sipTelemetry.connectionFailed({
              failureType: "transport",
              sipCode: code,
              transaction: "register",
              retryAfter,
            });
          }
        },
      },
    });

    const outcome = await Promise.race([registeredPromise, transportDropPromise, timeoutPromise]);
    if (outcome === REGISTRATION_TIMEOUT_SENTINEL) {
      throw new Error("REGISTRATION_TIMEOUT");
    }
    if (outcome === TRANSPORT_DROPPED_SENTINEL) {
      throw new Error("TRANSPORT_DROPPED");
    }
    // NB: isRegistered è impostato dal listener su RegistererState.Registered
  } catch (error) {
    console.error("Errore durante la configurazione SIP:", error);
    sipStore.isRegistered = false;
    if (!sipStore.connectionFailure) {
      const isTimeout = error instanceof Error && error.message === "REGISTRATION_TIMEOUT";
      sipStore.setConnectionFailure({
        type: isTimeout ? "timeout" : "transport",
        message: isTimeout ? TIMEOUT_FAILURE_MESSAGE : TRANSPORT_FAILURE_MESSAGE,
      });
      sipTelemetry.connectionFailed({
        failureType: isTimeout ? "timeout" : "transport",
        transaction: isTimeout ? "timeout" : "transport",
      });
    }
    throw error;
  } finally {
    if (timeoutHandle) clearTimeout(timeoutHandle);
    cleanupRaceListeners();
  }
};

export const getUserAgent = (): UserAgent | null => userAgent;

/**
 * Fotografia sincrona dello stato del transport e del registerer correnti.
 * Usata dalla recovery post-wake (GAIA-2587) per decidere se rifare il setup
 * senza dipendere dal flag `isRegistered` dello store, che può essere rimasto
 * `true` proprio nei casi di caduta silenziosa che vogliamo intercettare.
 */
export const getSipHealth = (): {
  hasUserAgent: boolean;
  transportConnected: boolean;
  registered: boolean;
} => ({
  hasUserAgent: userAgent !== null,
  transportConnected: userAgent?.transport.state === TransportState.Connected,
  registered: registerer?.state === RegistererState.Registered,
});

export const deregisterSIP = async () => {
  // Clear any pending failure state and mark transport as "not yet connected"
  // BEFORE tearing down the UserAgent. Otherwise userAgent.stop() will trigger
  // the transport-disconnect listener and set a spurious 'transport' failure
  // that the retry state machine would pick up, scheduling a silent re-login.
  wasEverConnected = false;
  // Deregistrazione voluta: silenzia l'handler di caduta registrazione così i
  // successivi stati Unregistered/Terminated NON vengano scambiati per una
  // perdita di registrazione da recuperare (GAIA-2587).
  intentionalUnregister = true;
  if (sipStore) {
    sipStore.clearConnectionFailure();
  }

  if (registerer) {
    try {
      await registerer.unregister();
    } catch (error) {
      console.error("Errore durante la deregistrazione:", error);
    }
    registerer = null;
  }

  if (userAgent) {
    try {
      await userAgent.stop();
    } catch (error) {
      console.error("Errore durante lo stop del UserAgent:", error);
    }
    userAgent = null;
  }

  if (sipStore) {
    sipStore.isRegistered = false;
  }
};
